import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import isNil from 'utils/isNil';
import styled from 'styled-components';
import { Row, Col } from 'antd';
import { CaretDownFilled } from '@ant-design/icons';
import { STIcon } from 'components/Brand/Icons/STIcon';
import Cards from 'components/Cards/Cards';
import CardsGroup from 'components/Cards/CardsGroup';
import { SectionLayout } from 'components/Sections/SectionLayout';
import './BlogCardGroupSection.less';
import ResourceHubFeaturedContent from 'components/ImageCardSections/ResourceHubFeaturedContent';
import { SearchInput } from 'components/Form/TextField/SearchInput';
import { taxonomyString } from 'utils/text';

const coronaMenu = [
  { title: 'All' },
  { title: 'News' },
  { title: 'Industry Trends' },
  { title: 'Webinar' },
];

const MenuItems = ({ items, category }) => {
  return (
    <div className="category-wrapper">
      <div className="st-header-container">
        <div className="st-center-menu">
          <ul role="menu" className="st-main-menu-wrapper">
            <MenuItem
              type={'simple-menu'}
              title={category}
              hasSubMenu={true}
              link={''}
              target={null}
            >
              <SimpleMenu>
                <Row gutter={42} type="flex">
                  {items.map((menu, index) => {
                    return (
                      <Col Col lg={12} md={12} sm={24} xs={24} key={index}>
                        <SimpleMenuItem
                          role="menuitem"
                          type={'simple-menu'}
                          icon={null}
                          title={menu.title}
                          link={
                            menu.title === 'All'
                              ? '/blog'
                              : `/blog/${menu.title
                                  .toLowerCase()
                                  .split(' ')
                                  .join('-')}`
                          }
                        />
                      </Col>
                    );
                  })}
                </Row>
              </SimpleMenu>
            </MenuItem>
          </ul>
        </div>
      </div>
    </div>
  );
};

export const MenuItem = ({ children, type, title, link, hasSubMenu, target, ...otherProps }) => {
  const [isOpened, setOpened] = useState(false);

  const menuItemClick = e => {
    setOpened(!isOpened);
  };
  return (
    <li
      className={`menu-item menu-item--${type === 'simple-menu' ? 'menu' : type}${
        hasSubMenu ? ' has-child' : ''
      }${isOpened ? ' menu-item--opened' : ''}`}
      onClick={menuItemClick}
    >
      {hasSubMenu ? (
        <span to={link}>
          {title}
          <CaretDownFilled />
        </span>
      ) : !isNil(link) ? (
        target === 'external' ? (
          <a href={link}>{title}</a>
        ) : (
          <Link to={link}>{title}</Link>
        )
      ) : (
        <a href={`tel:${title}`}>{title}</a>
      )}
      {children}
    </li>
  );
};

export const SimpleMenu = ({ children, ...otherProps }) => {
  return (
    <div className="simple-menu" {...otherProps}>
      {children}
    </div>
  );
};

export const SimpleMenuItem = ({ type, icon, title, link, target, ...otherProps }) => {
  return (
    <div className="simple-menu-item" {...otherProps}>
      {link ? (
        <Link to={link}>
          {type === 'icon-title-desc' && <STIcon className="simple-menu-item--icon" type={icon} />}
          <div className="simple-menu-item--content">
            <a className="simple-menu-item--title">{title}</a>
          </div>
        </Link>
      ) : (
        <div>
          {type === 'icon-title-desc' && <STIcon className="simple-menu-item--icon" type={icon} />}
          <div className="simple-menu-item--content">
            <a className="simple-menu-item--title">{title}</a>
          </div>
        </div>
      )}
    </div>
  );
};

const BlogCardGroupSection = ({
  menuText,
  content = [],
  allContent = [],
  hubType,
  featuredContent,
  category,
  allCategory = [],
  onSearch = () => {},
  slug,
}) => {
  const [allPosts] = useState(allContent);
  const [filteredPosts, setFilteredPosts] = useState(content);
  const blogCategory = ['All', ...allCategory].map(item => ({ title: item }));

  const handleSearch = term => {
    onSearch(term);

    if (!term) {
      setFilteredPosts(allPosts);
    } else {
      const fPosts = allPosts.filter(
        post =>
          post.title?.toLowerCase().includes(term?.toLowerCase()) ||
          post?.excerpt?.excerpt?.toLowerCase().includes(term?.toLowerCase())
      );
      setFilteredPosts(fPosts);
    }
  };

  return (
    <SectionLayout
      className="section-image-card-group--main-container"
      backgroundColor={'--blue-fourth'}
    >
      <MenuArea>
        <Row gutter={42}>
          <Col lg={12} md={12} sm={12} xs={24}>
            <Menu>
              <STIcon type="icon-category" />
              <span>{menuText}</span>
              <MenuItems
                items={hubType === 'coronavirus' ? coronaMenu : blogCategory}
                category={category}
              />
            </Menu>
          </Col>
          <Col lg={12} md={12} sm={12} xs={24} className="section-image-card-group--search">
            <SearchInput handleSearch={handleSearch} />
          </Col>
        </Row>
      </MenuArea>
      <BlogCardSection>
        {category === 'All' && featuredContent && !slug?.includes('/page/') && (
          <ResourceHubFeaturedContent featuredContent={featuredContent} />
        )}

        {filteredPosts?.length > 0 && (
          <CardsGroup cols={2}>
            {filteredPosts.map((data, index) => (
              <Cards
                type={'Image'}
                bodyType={'blog'}
                coverImage={data?.featuredImage?.image || data?.featuredImage}
                category={taxonomyString(data?.taxonomy)}
                tags={category === 'All' ? null : category}
                title={data?.title}
                description={data.excerpt?.excerpt}
                ctaTitle={'Learn more'}
                ctaLink={`/${hubType}/${data.slug}`}
                fullCoverImage={true}
                key={index}
              />
            ))}
          </CardsGroup>
        )}
      </BlogCardSection>
    </SectionLayout>
  );
};

const BlogCardSection = styled.div`
  .section-blog-card--card-container {
    margin-top: 60px;
    margin-bottom: 60px;

    .ant-col {
      .ant-card {
        justify-content: flex-start;

        .ant-card-cover {
          min-height: 321px;
          max-height: 321px;

          @media (max-width: 1200px) {
            min-height: 250px;
            max-height: 250px;
          }

          @media (max-width: 768px) {
            min-height: 215px;
            max-height: 215px;
            .card-cover--image {
              width: 100%;
              padding-bottom: 0;
              min-height: 215px !important;
              max-height: 215px !important;
            }
          }

          @media (max-width: 568px) {
            min-height: 250px;
            max-height: 250px;
          }

          .card-cover--image {
            width: 100%;
            padding-bottom: 56.25%;
            max-height: none;
          }
        }
      }
      @media (max-width: 768px) {
        margin-bottom: 60px;
        padding-left: 10px !important;
        padding-right: 10px !important;
      }

      @media (max-width: 576px) {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  }

  .blog-posts--show-more-btn-wrapper {
    text-align: center;
  }
`;

const MenuArea = styled.div`
  .ant-input {
    border-radius: 0;
    border: 1px solid #dae2e6;
    font-size: 20px;
    padding-left: 48px !important;
  }
  .ant-row {
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
    @media (max-width: 576px) {
      flex-direction: column;
    }
  }
  .ant-col {
    display: flex;
    justify-content: start;
    @media (max-width: 576px) {
      justify-content: center;
    }
  }
  .section-image-card-group--search {
    @media (min-width: 992px) {
      justify-content: flex-end;
    }
  }
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  max-width: fit-content;
  color: var(--neutral-text-color);
  font-size: 16px;
  @media (max-width: 576px) {
    margin-bottom: 30px;
  }
  span {
    margin: 0 14px 0 7px;
  }

  div {
    color: var(--blue-secondary);
    font-weight: 700;
  }
`;

export default BlogCardGroupSection;
